import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { Box } from "@mui/material";
import { AddVendorDrawer } from "../AddVendorDrawer/AddVendorDrawer";
import { useCommunityVendorsTabViewModel } from "./useCommunityVendorsTabViewModel";

export function CommunityVendorsTab({
  community: { id: communityId },
  isLoading,
}) {
  const { classes, theme } = useStyles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const {
    supplierAccounts,
    vendors,
    getSupplierAccounts,
    getVendors,
    addVendor,
    deleteVendor,
    isLoading: isViewModelLoading,
  } = useCommunityVendorsTabViewModel();

  useEffect(() => {
    if (communityId) {
      getSupplierAccounts();
      getVendors(communityId);
    }
  }, [communityId]);

  const handleDeleteVendor = async () => {
    await deleteVendor({
      id: selectedVendor.id,
      modifiedOn: selectedVendor.modifiedOn,
    });
    getVendors(communityId);
    setIsDeleteModalOpen(false);
  };

  const handleAddVendor = async ({ data }) => {
    await addVendor({
      data: {
        accountId: data.accountId,
        communityId,
      },
    });
    getVendors(communityId);
  };

  const columns = [
    {
      field: "accountName",
      headerName: "Supplier Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "supplierType",
      headerName: "Supplier Type",
      flex: 1,
      minWidth: 150,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedVendor(row);
        setIsDeleteModalOpen(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  const rows = vendors.map((vendor) => ({
    id: vendor.id,
    accountName: vendor.accountName || "Unknown",
    supplierType: vendor.supplierType,
    modifiedOn: vendor.modifiedOn,
  }));

  const vendorIds = new Set(vendors.map((v) => v.accountId));
  const filteredSupplierAccounts = useMemo(
    () => supplierAccounts.filter((acc) => !vendorIds.has(acc.id)) || [],
    [supplierAccounts, vendorIds]
  );

  return (
    <MiBox className={classes.viewContainer} height="inherit">
      <MiBuildPageHeader
        title="Suppliers"
        hasDivider={false}
        titleFontSize={18}
        actionComponent={
          <MiButton
            title="Add Supplier"
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={() => setIsModalOpen(true)}
            data-testid="add-vendor-btn"
          />
        }
      />

      <Box className={classes.content}>
        <MiList
          columns={columns}
          data={rows}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={getRowActions}
          emptyListProps={{
            text: "No Vendors found",
          }}
        />
      </Box>

      <AddVendorDrawer
        supplierAccounts={filteredSupplierAccounts}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        isLoading={isViewModelLoading}
        onSave={handleAddVendor}
      />

      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={isDeleteModalOpen}
        description={`Are you sure you want to remove ${selectedVendor?.accountName} from this community?`}
        disableClick={isLoading}
        handleNegative={() => setIsDeleteModalOpen(false)}
        handlePositive={handleDeleteVendor}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  viewContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
  },
}));
